/**
 * PemAssignmentPage — the interface for #348 (patients:pem:* channels).
 *
 * Carlos assigns patients to Patient Experience Managers as routine workflow —
 * every doctor- or employee-originated contact — so this is a working tool, not
 * an occasional admin page.
 *
 * ONE VIEW: find a patient, see who is accountable for them, assign / reassign /
 * unassign. There is deliberately no by-PEM view; the reasoning is in the PR body
 * under "Why there is no by-PEM view" rather than living here as a disabled branch
 * somebody would later wire to the wrong data source.
 *
 * ⚠ expectedPemUserId IS THE POINT OF THIS SCREEN.
 * `patients:pem:assign` takes an OPTIONAL `expectedPemUserId`. Supplying it makes
 * the call a compare-and-set; omitting it skips the check entirely (routes/pem.js:368
 * — `if (hasExpectation && from !== expected)`). A screen ALWAYS knows what it
 * displayed, so it ALWAYS passes what it displayed: `submitAssign` sends
 * `owner.assignedPemUserId`, the value rendered to the user, never a value re-read
 * at submit time. If two people have the same patient open and one assigns, the
 * other's submit is REFUSED instead of silently winning. Omitting the field would
 * compile, pass tests and look identical in the happy path — which is why it is
 * called out here and asserted in tests/react/pemAssignmentPage.test.js.
 *
 * ⚠ NO WRITE WITHOUT A READ YOU CAN TRUST.
 * If the owner read failed or was refused, the assign controls are DISABLED. An
 * assign whose `expectedPemUserId` is unknown is an UNGUARDED write: it succeeds,
 * it looks identical to a correct one, and it silently overwrites whatever the
 * other person just did. That is the blind-bulk-move failure wearing different
 * clothes, so it gets the same answer — refuse to write rather than write blind.
 *
 * ⚠ FOUR OWNER STATES, deliberately not collapsed:
 *     loading    — still fetching
 *     unreadable — the read failed or was refused; assign DISABLED, reason shown
 *     none       — a KNOWN absence; assign ENABLED, expectedPemUserId is a known null
 *     owned      — assign ENABLED, expectedPemUserId is the displayed owner
 * Collapsing "unreadable" into "none" is the misread that overwrites an owner you
 * merely were not allowed to see. `none` is also the NORMAL case today —
 * patient_journey_state has 0 rows in production — so it must read as a stated
 * absence, never as a blank that could be mistaken for a loading state.
 *
 * Permissions required — FOUR, measured not assumed (see the PR body):
 *   patients:reassign  to assign            (patients:pem:assign)
 *   pem:read           to read the owner    (patients:pem:get)
 *   crm:read           to search            (crm:filtered-list)
 *   crm:reassign       to list assignees    (crm:assignable-users)
 * public/apiClient.js gates each channel client-side and agrees with the server, so
 * a partial grant half-works — which is precisely what the disabled-write rules
 * above turn into a visible refusal rather than a silently dead screen.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Users, Search, UserCheck, UserX, AlertCircle, Loader2,
  Shield, RefreshCcw, UserMinus,
} from "lucide-react";

function hasPerm(user, p) { return new Set((user && user.permissions) || []).has(p); }

const byName = (a, b) =>
  (a.display_name || a.username || "").localeCompare(
    b.display_name || b.username || "", undefined, { sensitivity: "base" });

const contactLabel = (c) =>
  [c.first_name, c.last_name].filter(Boolean).join(" ").trim() || `Contact ${c.id}`;

export default function PemAssignmentPage({ user, onNavigate }) {
  const canAssign = hasPerm(user, "patients:reassign");
  const canReadPem = hasPerm(user, "pem:read");

  const [staff, setStaff] = useState([]);
  // loading | unreadable | empty | ready — "empty" is a known absence,
  // "unreadable" is a refusal. Collapsing them is the misread that leaves the
  // screen silently dead with no explanation.
  const [staffState, setStaffState] = useState("loading");
  const [staffWhy, setStaffWhy] = useState(null);
  const [error, setError] = useState(null);

  const [term, setTerm] = useState("");
  const [results, setResults] = useState(null);   // null = never searched
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [owner, setOwner] = useState(null);       // { state, assignedPemUserId, ... }
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const inFlight = useRef(false);   // synchronous double-submit guard — see submitAssign

  // ⚠ THE STAFF LIST IS A READ, AND A WRITE DEPENDS ON IT — so it gets the same
  // discipline as the owner read. An empty picker reads as "there are no PEMs";
  // rendering a REFUSAL that way is the identical misread this screen refuses to
  // make for the owner, and it is worse here because `target` can then never be
  // set, so Assign stays disabled forever with nothing on screen to explain why.
  //
  // MEASURED, not assumed: `crm:assignable-users` is gated on `crm:reassign`
  // (routes/crm.js — `rbac.secureHandler("crm:reassign", …)`), which is a FOURTH
  // permission beyond the three this screen otherwise needs. See the PR body.
  //
  // There is deliberately NO fallback. The obvious candidates are both wrong:
  //   - `users:list` DOES NOT EXIST as a channel (the fallback I first wrote
  //     called nothing at all, and failed silently — which is how it survived).
  //   - `crm:staff:list` IS reachable on `crm:read`, but it reads the `crm_staff`
  //     table, which has NO user_id column; its SERIAL ids are unrelated to
  //     `users.id`. `assigned_pem_user_id` is an FK to `users(id)`, so feeding
  //     crm_staff ids into the picker would assign patients to an ARBITRARY
  //     DIFFERENT PERSON who happens to share that integer. Silently.
  // A fallback that fails silently is worse than a stated refusal.
  const loadStaff = useCallback(async () => {
    setStaffState("loading");
    try {
      const r = await window.zignagenixAPI.invoke("crm:assignable-users");
      if (Array.isArray(r)) {
        setStaff(r);
        setStaffState(r.length ? "ready" : "empty");   // an absence, distinct from a refusal
        return;
      }
      setStaff([]);
      setStaffState("unreadable");
      setStaffWhy((r && (r.message || r.code)) || "The staff list could not be read.");
    } catch (e) {
      // Not swallowed. A picker that cannot be read blocks the write.
      setStaff([]);
      setStaffState("unreadable");
      setStaffWhy((e && e.message) || "The staff list could not be read.");
    }
  }, []);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  const loadOwner = useCallback(async (contactId) => {
    setOwner({ state: "loading" });
    if (!canReadPem) { setOwner({ state: "unreadable", why: "You do not hold pem:read." }); return; }
    try {
      const r = await window.zignagenixAPI.invoke("patients:pem:get", contactId);
      if (!r || r.error) {
        // ⚠ A refusal is NOT an absence. Any error — mapped code, unmapped code,
        // or an empty response — lands here as `unreadable`, never as "no PEM".
        setOwner({ state: "unreadable", why: (r && (r.message || r.code)) || "The owner could not be read." });
        return;
      }
      setOwner({
        state: r.assignedPemUserId != null ? "owned" : "none",
        assignedPemUserId: r.assignedPemUserId != null ? r.assignedPemUserId : null,
        assignedPemName: r.assignedPemName || null,
        hasJourneyRow: !!r.hasJourneyRow,
      });
    } catch (err) {
      setOwner({ state: "unreadable", why: (err && err.message) || "The owner could not be read." });
    }
  }, [canReadPem]);

  async function runSearch(e) {
    if (e) e.preventDefault();
    const q = term.trim();
    if (!q) return;
    setSearching(true); setError(null);
    try {
      // Reuse the CRM's EXISTING contact search. No second search is written here.
      const r = await window.zignagenixAPI.invoke("crm:filtered-list", {
        resource: "contacts", search: q, pageSize: 50,
      });
      if (r && r.error) { setError(r.message || r.code); setResults([]); }
      else setResults(Array.isArray(r && r.rows) ? r.rows : []);
    } catch (err) { setError((err && err.message) || "Search failed"); setResults([]); }
    setSearching(false);
  }

  function pick(contact) {
    setSelected(contact); setConflict(null); setTarget(""); setReason("");
    loadOwner(contact.id);
  }

  // ⚠ The gate for every write on this screen. `owned` and `none` are both
  // TRUSTED reads — one is a known id, the other a known null. `loading` and
  // `unreadable` are not, so nothing may be written from them.
  const ownerIsTrusted = !!owner && (owner.state === "owned" || owner.state === "none");
  // A pick is only possible from a list we could actually read. `empty` is a
  // trusted read (there genuinely are no assignable users) — it yields no
  // options, but it is not a refusal and does not need a refusal's explanation.
  const staffIsTrusted = staffState === "ready" || staffState === "empty";
  const canWrite = ownerIsTrusted && staffIsTrusted;

  async function submitAssign(unassign) {
    if (!selected || !canWrite) return;
    // ⚠ Found by the adversarial pass, not by the suite: `setSaving(true)` does not
    // disable the button until React re-renders, so three fast clicks sent THREE
    // assigns. The server's compare-and-set meant only the first landed and the
    // rest came back ASSIGNMENT_CONFLICT — safe, but it showed a conflict banner
    // after a successful assign and made three calls. A ref flips synchronously,
    // so the second click is refused in the same tick.
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true); setError(null); setConflict(null);
    try {
      // ⚠ THE WHOLE POINT: pass what was DISPLAYED. `owner.assignedPemUserId` is
      // the value this screen rendered; it is NOT re-read at submit time. When the
      // state is `none` this is a known null, which the handler compares with
      // IS NOT DISTINCT FROM — so "was unassigned, still unassigned" is verified
      // rather than assumed.
      const payload = {
        contactId: selected.id,
        toPemUserId: unassign ? null : Number(target),
        expectedPemUserId: owner.state === "owned" ? owner.assignedPemUserId : null,
      };
      // The single assign does NOT require a reason (only bulk does, via
      // MISSING_REASON at routes/pem.js:435), so this stays optional and is only
      // sent when the user actually wrote one.
      if (reason.trim()) payload.reason = reason.trim();

      const r = await window.zignagenixAPI.invoke("patients:pem:assign", payload);

      if (r && r.error && r.code === "ASSIGNMENT_CONFLICT") {
        // Re-render from the truth the server returned. NO auto-retry: a silent
        // retry races into the same conflict and produces the overwrite the guard
        // exists to prevent. The person decides again with the truth in front of them.
        const who = staff.find((s) => Number(s.id) === Number(r.currentPemUserId));
        const whoName = who ? (who.display_name || who.username) : null;
        setConflict({ currentPemUserId: r.currentPemUserId != null ? r.currentPemUserId : null, currentPemName: whoName });
        setOwner(r.currentPemUserId != null
          ? { state: "owned", assignedPemUserId: r.currentPemUserId, assignedPemName: whoName, hasJourneyRow: true }
          : { state: "none", assignedPemUserId: null, assignedPemName: null, hasJourneyRow: true });
        setSaving(false); inFlight.current = false;
        return;
      }
      if (r && r.error) { setError(r.message || r.code); setSaving(false); inFlight.current = false; return; }

      setTarget(""); setReason("");
      await loadOwner(selected.id);
    } catch (err) { setError((err && err.message) || "Assignment failed"); }
    setSaving(false); inFlight.current = false;
  }

  if (!canAssign) {
    return (
      <div className="page">
        <div style={emptyBox}>
          <Shield size={48} style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 16 }}>You do not have permission to assign Patient Experience Managers.</p>
          <p style={{ fontSize: 13 }}>This screen requires <code>patients:reassign</code>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>

      <div className="page-header">
        <h1 className="page-title">PEM Assignment</h1>
        <p className="page-subtitle">Who is accountable for each patient.</p>
      </div>

      {!canReadPem && (
        <div style={warnBox}>
          <AlertCircle size={14} />
          <span>
            You can act, but not read the current owner — <code>pem:read</code> is missing.
            Assignment is disabled until that is granted, because an assign sent without the
            current owner would overwrite it unchecked.
          </span>
        </div>
      )}

      {staffState === "unreadable" && (
        <div style={warnBox}>
          <AlertCircle size={14} />
          <span>
            The list of people you can assign to could not be read, so assignment is
            disabled. This is a refusal, not an empty list — <code>crm:assignable-users</code>{" "}
            is gated on <code>crm:reassign</code>. {staffWhy}
          </span>
        </div>
      )}
      {staffState === "empty" && (
        <div style={warnBox}>
          <AlertCircle size={14} /> There are no active users available to assign to.
        </div>
      )}

      {error && <div style={errBox}><AlertCircle size={14} /> {error}</div>}

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ ...card, flex: "1 1 420px", minWidth: 320, padding: 14 }}>
          <form onSubmit={runSearch} style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input
              style={{ ...input, flex: 1 }}
              placeholder="Search name, email or phone"
              aria-label="Search contacts"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
            <button type="submit" style={btnPrimary} disabled={searching || !term.trim()}>
              {searching ? <Loader2 size={14} className="spin" /> : <Search size={14} />} Search
            </button>
          </form>

          {results === null && (
            <div style={emptyBox}><Search size={32} style={{ marginBottom: 8 }} /><p>Search for a patient to see who is accountable for them.</p></div>
          )}
          {results !== null && results.length === 0 && !searching && (
            <div style={emptyBox}><p>No contacts matched that search.</p></div>
          )}
          {results !== null && results.length > 0 && (
            <div className="responsive-scroll">
              <table style={{ width: "100%", minWidth: 420, borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Contact</th><th style={th}>Email</th><th style={th} /></tr></thead>
                <tbody>
                  {results.map((c) => (
                    <tr key={c.id}>
                      <td style={td}>{contactLabel(c)}</td>
                      <td style={td}>{c.email || "—"}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button style={btnGhost} onClick={() => pick(c)}>Select</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ ...card, flex: "1 1 380px", minWidth: 320, padding: 14 }}>
          {!selected && (
            <div style={emptyBox}><Users size={32} style={{ marginBottom: 8 }} /><p>Select a patient to view or change their PEM.</p></div>
          )}

          {selected && (
            <>
              <h3 style={{ margin: "0 0 10px", fontSize: 15 }}>{contactLabel(selected)}</h3>

              <div style={{ marginBottom: 14 }}>
                <div style={label}>Current PEM</div>
                <OwnerLine owner={owner} />
              </div>

              {owner && owner.state === "unreadable" && (
                <div style={warnBox}>
                  <AlertCircle size={14} />
                  <span>Assignment is disabled because the current owner could not be read. {owner.why}</span>
                </div>
              )}

              {conflict && (
                <div style={warnBox}>
                  <AlertCircle size={14} />
                  <span>
                    Someone else changed this while you had it open. It is now held by{" "}
                    <strong>{conflict.currentPemName || (conflict.currentPemUserId == null ? "nobody" : `user ${conflict.currentPemUserId}`)}</strong>.
                    Your change was not applied — choose again.
                  </span>
                </div>
              )}

              <div style={{ marginBottom: 10 }}>
                <label style={label} htmlFor="pem-target">Assign to</label>
                <select id="pem-target" style={input} value={target}
                        disabled={!canWrite}
                        onChange={(e) => setTarget(e.target.value)}>
                  <option value="">Select a PEM…</option>
                  {[...staff].sort(byName).map((s) => (
                    <option key={s.id} value={s.id}>{s.display_name || s.username}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={label} htmlFor="pem-reason">Reason (optional)</label>
                <input id="pem-reason" style={input} value={reason} disabled={!canWrite}
                       onChange={(e) => setReason(e.target.value)} placeholder="e.g. caseload rebalance" />
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  style={{ ...btnPrimary, opacity: (!target || saving || !canWrite) ? 0.5 : 1,
                           cursor: (!target || saving || !canWrite) ? "not-allowed" : "pointer" }}
                  disabled={!target || saving || !canWrite}
                  onClick={() => submitAssign(false)}
                >
                  {saving ? <Loader2 size={14} className="spin" /> : <UserCheck size={14} />} Assign
                </button>
                {owner && owner.state === "owned" && (
                  <button style={btnGhostDanger} disabled={saving || !canWrite} onClick={() => submitAssign(true)}>
                    <UserMinus size={14} /> Unassign to pool
                  </button>
                )}
                <button style={btnSecondary} disabled={saving} onClick={() => loadOwner(selected.id)}>
                  <RefreshCcw size={14} /> Retry read
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function OwnerLine({ owner }) {
  if (!owner || owner.state === "loading") {
    return <div style={ownerBox}><Loader2 size={14} className="spin" /> Loading…</div>;
  }
  if (owner.state === "unreadable") {
    return <div style={{ ...ownerBox, color: "#8b0000" }}><Shield size={14} /> Could not read the current PEM.</div>;
  }
  if (owner.state === "none") {
    // A KNOWN absence, said out loud. Most contacts have no journey row — that is
    // the normal case today, not an error and not a loading state.
    return <div style={{ ...ownerBox, color: "#6b7280" }}><UserX size={14} /> No PEM assigned — this patient is in the pool.</div>;
  }
  return (
    <div style={{ ...ownerBox, color: "#1F2937" }}>
      <UserCheck size={14} />
      {owner.assignedPemName || `User ${owner.assignedPemUserId}`}
    </div>
  );
}

const card = { background: "#fff", border: "1px solid #e1e6ed", borderRadius: 6 };
const th = { textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.03em", borderBottom: "1px solid #e1e6ed", background: "#f7f9fc" };
const td = { padding: "8px 12px", borderBottom: "1px solid #f0f3f7", color: "#1F2937", verticalAlign: "middle" };
const input = { width: "100%", padding: "7px 10px", fontSize: 13, border: "1px solid #DEE2E6", borderRadius: 6, color: "#1F2937", background: "#fff" };
const label = { display: "block", fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 4 };
const btnPrimary = { padding: "6px 12px", fontSize: 12, fontWeight: 600, background: "#2E8B98", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 };
const btnSecondary = { padding: "6px 12px", fontSize: 12, fontWeight: 600, background: "#fff", color: "#5a6478", border: "1px solid #DEE2E6", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 };
const btnGhost = { padding: "3px 9px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#2E8B98", border: "1px solid #cfe3e6", borderRadius: 5, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 };
const btnGhostDanger = { ...btnGhost, padding: "6px 12px", fontSize: 12, color: "#b00020", border: "1px solid #f0c0c0" };
const errBox = { display: "flex", alignItems: "center", gap: 6, margin: "0 0 12px", padding: "8px 12px", borderRadius: 4, fontSize: 12.5, background: "#fde8e8", border: "1px solid #f5a5a5", color: "#8b0000" };
const warnBox = { display: "flex", alignItems: "center", gap: 6, margin: "0 0 12px", padding: "8px 12px", borderRadius: 4, fontSize: 12.5, background: "#fff8e1", border: "1px solid #f0d68a", color: "#7a5b00" };
const ownerBox = { display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 600, padding: "6px 0" };
const emptyBox = { textAlign: "center", padding: "40px 16px", color: "#9ca3af", fontSize: 13 };
